/**
 * BadgeSelector Component
 * Multi-select interface for choosing badges from the available badge catalog
 * Fetches badges dynamically from the API - no hardcoding
 */

import { useState, useMemo } from 'react';
import { useAllAdminBadges } from '../../hooks/useBadgeApi';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { BadgeIcon } from '../badges/BadgeIcon';
import type { BadgeDefinition } from '../../lib/types/badge';

interface BadgeSelectorProps {
  selectedBadgeIds: string[];
  onChange: (badgeIds: string[]) => void;
  disabled?: boolean;
}

interface BadgeCardProps {
  badge: BadgeDefinition;
  isSelected: boolean;
  onToggle: (badgeId: string) => void;
  disabled?: boolean;
}

function BadgeCard({
  badge,
  isSelected,
  onToggle,
  disabled = false,
}: BadgeCardProps) {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle(badge.badgeId);
    }
  };

  return (
    <div
      role="checkbox"
      aria-checked={isSelected}
      aria-label={`${badge.name}: ${badge.description}`}
      tabIndex={disabled ? -1 : 0}
      className={`p-3 rounded-lg border-2 cursor-pointer transition-colors ${
        isSelected
          ? 'border-sky-500 bg-sky-50 dark:bg-sky-900/20'
          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      onClick={() => !disabled && onToggle(badge.badgeId)}
      onKeyDown={handleKeyDown}
    >
      <div className="flex items-center space-x-3">
        {/* Badge Icon.

            This was a bespoke `icon_url ? <img> : <glyph>` branch — the one
            renderer in the app that did not go through BadgeIcon, so it also
            missed the empty-src handling, the load/error states and, now, the
            imageless distinction. Using the shared component means there is
            one place left where a badge icon is drawn wrongly, not two. */}
        <div className="flex-shrink-0 w-8 h-8">
          <BadgeIcon
            src={badge.icon_url}
            alt=""
            size="sm"
            imageless={badge.imageless}
          />
        </div>

        {/* Badge Info */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {badge.name}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {badge.badgeId}
          </p>
        </div>

        {/* Selection Indicator */}
        <div className="flex-shrink-0">
          {isSelected ? (
            <svg
              className="w-5 h-5 text-sky-600"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
          ) : (
            <div className="w-5 h-5 border-2 border-gray-300 dark:border-gray-600 rounded-full" />
          )}
        </div>
      </div>
    </div>
  );
}

export function BadgeSelector({
  selectedBadgeIds,
  onChange,
  disabled = false,
}: BadgeSelectorProps) {
  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // The whole catalogue, every page (#2349): a badge on a later page would
  // otherwise have no card, and would be reported as no longer existing.
  const { data, isLoading, error, isError } = useAllAdminBadges();

  const badges = data?.badges ?? [];

  // Selected ids with no badge in the catalogue (#2349): a badge deleted
  // after the group was written. They have no card, so without this list
  // they would be invisible here and posted back on every save. Shown only
  // when the whole catalogue was read.
  const missingSelectedIds = useMemo(() => {
    if (!data || data.hasMore) return [];
    const known = new Set(data.badges.map(b => b.badgeId));
    // Distinct, so each row has its own key.
    return [...new Set(selectedBadgeIds.filter(id => !known.has(id)))];
  }, [data, selectedBadgeIds]);

  // Filter badges based on search query
  const filteredBadges = useMemo(() => {
    if (!badges.length || !searchQuery) {
      return badges;
    }

    const query = searchQuery.toLowerCase();
    return badges.filter(
      (badge: BadgeDefinition) =>
        badge.name.toLowerCase().includes(query) ||
        badge.badgeId.toLowerCase().includes(query)
    );
  }, [badges, searchQuery]);

  const handleToggle = (badgeId: string) => {
    if (disabled) return;

    const newSelection = selectedBadgeIds.includes(badgeId)
      ? selectedBadgeIds.filter(id => id !== badgeId)
      : [...selectedBadgeIds, badgeId];

    onChange(newSelection);
  };

  const handleSelectAll = () => {
    if (disabled || !filteredBadges.length) return;

    // Check if all filtered badges are selected
    const filteredBadgeIds = filteredBadges.map(b => b.badgeId);
    const allFilteredSelected = filteredBadgeIds.every(id =>
      selectedBadgeIds.includes(id)
    );

    if (allFilteredSelected) {
      // Deselect all filtered badges
      onChange(selectedBadgeIds.filter(id => !filteredBadgeIds.includes(id)));
    } else {
      // Select all filtered badges (preserving existing selections)
      const newSelection = [
        ...selectedBadgeIds.filter(id => !filteredBadgeIds.includes(id)),
        ...filteredBadgeIds,
      ];
      onChange(newSelection);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-24 border border-gray-200 dark:border-gray-600 rounded-lg">
        <LoadingSpinner size="md" />
        <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
          Loading badges...
        </span>
      </div>
    );
  }

  // Error state - fail fast, show clear error
  if (isError) {
    return (
      <div className="p-4 border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 rounded-lg">
        <p className="text-sm text-red-600 dark:text-red-400 font-medium">
          Failed to load badges
        </p>
        <p className="text-xs text-red-500 dark:text-red-400 mt-1">
          {error instanceof Error ? error.message : 'Unknown error occurred'}
        </p>
      </div>
    );
  }

  const missingSelected =
    missingSelectedIds.length > 0 ? (
      <div
        className="mb-3 p-3 border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 rounded-lg"
        role="group"
        aria-label="Selected badges that no longer exist"
      >
        <p className="text-xs font-medium text-amber-800 dark:text-amber-300">
          No longer exist: redemption skips these. Remove them from the group.
        </p>
        <ul className="mt-2 space-y-1">
          {missingSelectedIds.map(id => (
            <li key={id} className="flex items-center justify-between text-sm">
              <code className="text-amber-900 dark:text-amber-200">{id}</code>
              <button
                type="button"
                onClick={() => handleToggle(id)}
                disabled={disabled}
                aria-label={`Remove ${id}`}
                className="text-xs font-medium text-amber-800 hover:text-amber-600 dark:text-amber-300 dark:hover:text-amber-200 disabled:opacity-50"
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      </div>
    ) : null;

  // Empty state - no badges exist
  if (badges.length === 0) {
    return (
      <div>
        {missingSelected}
        <div className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No badges available
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            Create badges in the Badge Management section first
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {missingSelected}
      {/* Search Input */}
      <div className="mb-3">
        <input
          type="text"
          placeholder="Search badges..."
          aria-label="Search badges"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          disabled={disabled}
          className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 dark:bg-gray-800 dark:text-gray-100 disabled:opacity-50"
        />
      </div>

      {/* Empty Search Results */}
      {filteredBadges.length === 0 && searchQuery && (
        <div className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No matching badges
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            No badges match "{searchQuery}"
          </p>
        </div>
      )}

      {/* Selection Controls and Badge Grid - only show if we have filtered results */}
      {filteredBadges.length > 0 && (
        <>
          {/* Selection Controls */}
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {
                filteredBadges.filter(b => selectedBadgeIds.includes(b.badgeId))
                  .length
              }{' '}
              of {filteredBadges.length} selected
            </p>
            <button
              type="button"
              onClick={handleSelectAll}
              disabled={disabled}
              className="text-xs font-medium text-sky-600 hover:text-sky-500 dark:text-sky-400 dark:hover:text-sky-300 disabled:opacity-50"
            >
              {filteredBadges.every(b => selectedBadgeIds.includes(b.badgeId))
                ? 'Deselect All'
                : 'Select All'}
            </button>
          </div>

          {/* Badge Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
            {filteredBadges.map(badge => (
              <BadgeCard
                key={badge.badgeId}
                badge={badge}
                isSelected={selectedBadgeIds.includes(badge.badgeId)}
                onToggle={handleToggle}
                disabled={disabled}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
